"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "../../lib/cva";
import * as React from "react";
import { useMediaQuery } from "../../hooks/use-media-query";
import { Icon } from "./icon";
import { AVATAR_SIZE_PX } from "../../particles/entity-group/entity-avatar";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Input } from "./input";
import { ScrollArea } from "./scroll-area";
import { Separator } from "./separator";
import {
  Sheet,
  SheetDescription,
  SheetHeader,
  SheetPopup,
  SheetTitle,
} from "./sheet";
import { Skeleton } from "./skeleton";
import type { Omittable } from "../../lib/props";
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "./tooltip";

const SIDEBAR_COOKIE_NAME: string = "sidebar_state";
const SIDEBAR_COOKIE_MAX_AGE: number = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH: string = "16rem";
const SIDEBAR_WIDTH_MOBILE: string = "18rem";
const SIDEBAR_WIDTH_ICON: string = "3rem";
const SIDEBAR_KEYBOARD_SHORTCUT: string = "b";

/**
 * A row reserves ONE leading-visual column, and everything that leads a row
 * lands in it: a bare glyph, an avatar, a tinted icon tile, an entity chip.
 * The column is the avatar footprint, because that is the widest of them — an
 * avatar circle has to out-measure an icon glyph to read at the same weight
 * (`AVATAR_SIZE_MAP` in the consumer's entity visual is where that trade is
 * made).
 *
 * Reserving it on the ROW is what makes alignment automatic: a glyph narrower
 * than the column centers inside it instead of sitting flush left, so glyphs
 * line up down the column and so do the labels after them — without any row
 * knowing what kind of visual its neighbours use.
 *
 * Read from the avatar's own size table, so the column cannot drift from the
 * circles it is sized for.
 */
const ROW_VISUAL_VAR = "--sidebar-row-visual";

export const sidebarMenuButtonVariants = cva(
  cn(
    "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-sm p-2 text-left text-sm text-sidebar-foreground outline-hidden inset-ring-ring transition-[width,height] hover:bg-wash-hover focus-visible:inset-ring-2 active:bg-wash-active active:text-sidebar-foreground disabled:pointer-events-none disabled:opacity-64 aria-disabled:pointer-events-none aria-disabled:opacity-64 data-[active=true]:bg-wash-selected data-[active=true]:text-sidebar-foreground data-[active=true]:hover:bg-wash-selected-hover data-popup-open:hover:bg-wash-hover group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! [&>span:last-child]:truncate [&>svg:not([class*=\'size-\'])]:size-4 [&>svg]:shrink-0",
    // The ROW is the hover target, not just the button. A row's trailing
    // action (`SidebarMenuAction`) is a sibling of this button, so a pointer
    // moving onto it leaves the button and a self-`:hover` wash drops — the
    // row appears to un-hover under the very control the user is reaching
    // for. The wash therefore keys off the ITEM's hover, and holds while the
    // action's menu is open (the pointer leaves for a portalled popup, which
    // is no descendant of anything here). The `data-[active=true]` twins
    // carry the extra attribute selector that outranks the plain rung, so a
    // selected row still hovers to `selected-hover` rather than falling back
    // to the resting wash.
    "group-hover/menu-item:bg-wash-hover data-[active=true]:group-hover/menu-item:bg-wash-selected-hover",
    "group-has-[[data-popup-open]]/menu-item:bg-wash-hover data-[active=true]:group-has-[[data-popup-open]]/menu-item:bg-wash-selected-hover",
    // Room for the row's trailing action (`SidebarMenuAction`, a 20px square
    // at right-1 → 32px of end padding). An always-shown action holds the
    // room permanently. A hover-revealed one holds it ONLY while it shows —
    // hover, keyboard focus within the row, or its open popup — so the
    // resting label runs to the row's edge and re-truncates under the "…"
    // the moment it appears (an empty standing slot on every row was
    // rejected). The padding snaps with the action: no transition, so the
    // ellipsis never crawls. Coarse pointers never see the action, so they
    // never pay for it — a tap's sticky :hover must not pad the row.
    "group-has-[[data-sidebar=menu-action]:not([data-reveal=hover])]/menu-item:pe-8",
    "pointer-fine:group-hover/menu-item:group-has-[[data-reveal=hover]]/menu-item:pe-8",
    "pointer-fine:group-focus-within/menu-item:group-has-[[data-reveal=hover]]/menu-item:pe-8",
    "pointer-fine:group-has-[[data-reveal=hover][data-popup-open]]/menu-item:pe-8",
    // The three-rung ink ramp for a row's DEFAULT glyph: at rest the icon
    // sits a step below the (already muted) label; hover lifts it to the
    // label's ink; the active row lifts both to the full sidebar foreground.
    // The `[class^='text-']):not([class*='_text-']` guard leaves explicitly-colored icons (an
    // entity/stage glyph's `text-c`) alone, and nested visuals (chips, tiles)
    // are never direct `>svg` children, so the ramp cannot touch them.
    "[&>svg:not([class^=\'text-\']):not([class*=\'_text-\'])]:text-muted-foreground not-data-[active=true]:hover:[&>svg:not([class^=\'text-\']):not([class*=\'_text-\'])]:text-muted-foreground not-data-[active=true]:data-popup-open:[&>svg:not([class^=\'text-\']):not([class*=\'_text-\'])]:text-muted-foreground data-[active=true]:[&>svg:not([class^=\'text-\']):not([class*=\'_text-\'])]:text-sidebar-foreground",
    // The glyph rides the same item-scoped hover as the wash above, or it
    // would sit a rung dark on a row that is visibly hovered.
    "not-data-[active=true]:group-hover/menu-item:[&>svg:not([class^=\'text-\']):not([class*=\'_text-\'])]:text-muted-foreground",
    // The reserved leading-visual column. A visual already at least that wide
    // (an entity chip, an avatar) is untouched; a narrower glyph centers in it.
    // The min-width alone holds the column: no `shrink-0`, because a leading
    // entity chip carries the row's LABEL and must shrink past its text so
    // the label truncates instead of running under the trailing action.
    "[&>:first-child]:min-w-(--sidebar-row-visual)",
    // Collapsed to icons the row IS the glyph — there is no column to hold.
    "group-data-[collapsible=icon]:[&>:first-child]:min-w-0",
  ),
  {
    defaultVariants: {
      size: "default",
      variant: "default",
    },
    variants: {
      size: {
        default: "h-7 text-sm",
        lg: "h-12 text-sm group-data-[collapsible=icon]:p-0!",
        sm: "h-7 text-xs",
      },
      variant: {
        default: "hover:bg-wash-hover",
        outline:
          "bg-sidebar shadow-[0_0_0_1px_var(--sidebar-border)] hover:bg-wash-hover hover:shadow-[0_0_0_1px_var(--sidebar-border-strong)]",
      },
    },
  },
);

export type SidebarContextProps = {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
};

export const SidebarContext: React.Context<SidebarContextProps | null> =
  React.createContext<SidebarContextProps | null>(null);

export function useSidebar(): SidebarContextProps {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }

  return context;
}

export function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<"div"> & Omittable<{
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}>): React.ReactElement {
  const isMobile = useMediaQuery("max-md");
  const [openMobile, setOpenMobile] = React.useState(false);

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = React.useCallback(
    async (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === "function" ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      // This sets the cookie to keep the sidebar state.
      await cookieStore.set({
        expires: Date.now() + SIDEBAR_COOKIE_MAX_AGE * 1000,
        name: SIDEBAR_COOKIE_NAME,
        path: "/",
        value: String(openState),
      });
    },
    [setOpenProp, open],
  );

  // Helper to toggle the sidebar.
  const toggleSidebar = React.useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
  }, [isMobile, setOpen]);

  // Adds a keyboard shortcut to toggle the sidebar.
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);

  // We add a state so that we can do data-state="expanded" or "collapsed".
  // This makes it easier to style the sidebar with Tailwind classes.
  const state = open ? "expanded" : "collapsed";

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      isMobile,
      open,
      openMobile,
      setOpen,
      setOpenMobile,
      state,
      toggleSidebar,
    }),
    [state, open, setOpen, isMobile, openMobile, toggleSidebar],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <div
        className={cn(
          "group/sidebar-wrapper flex min-h-svh w-full has-data-[variant=inset]:bg-sidebar",
          className,
        )}
        data-slot="sidebar-wrapper"
        style={
          {
            "--sidebar-width": SIDEBAR_WIDTH,
            "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
            ...style,
          } as React.CSSProperties
        }
        {...props}
      >
        {children}
      </div>
    </SidebarContext.Provider>
  );
}

// The desktop gap element: an in-flow spacer that reserves the sidebar's
// width while the panel itself is fixed. `floating`/`inset` add the p-2
// frame's width to the icon-collapsed rail.
export const sidebarGapVariants = cva(
  cn(
    "relative w-(--sidebar-width) bg-transparent transition-[width] duration-base ease-linear",
    "group-data-[collapsible=offcanvas]:w-0",
    "group-data-[side=right]:rotate-180",
  ),
  {
    variants: {
      variant: {
        sidebar: "group-data-[collapsible=icon]:w-(--sidebar-width-icon)",
        floating:
          "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]",
        inset:
          "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]",
      },
    },
  },
);

// The fixed desktop panel. `side` picks which edge it pins to (and slides
// off of when off-canvas); `floating`/`inset` trade the plain `sidebar`
// border for the p-2 frame, widened by the frame plus its 2px of borders
// when icon-collapsed.
export const sidebarContainerVariants = cva(
  "fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) transition-[left,right,width] duration-base ease-linear md:flex",
  {
    variants: {
      side: {
        left: "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]",
        right:
          "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
      },
      // Adjust the padding for floating and inset variants.
      variant: {
        sidebar:
          "group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l",
        floating:
          "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]",
        inset:
          "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]",
      },
    },
  },
);

export function Sidebar({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  className,
  children,
  ...props
}: React.ComponentProps<"div"> &
  Omittable<
    VariantProps<typeof sidebarContainerVariants> & {
      collapsible?: "offcanvas" | "icon" | "none";
    }
  >): React.ReactElement {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

  if (collapsible === "none") {
    return (
      <div
        className={cn(
          "flex h-full w-(--sidebar-width) flex-col bg-sidebar text-sidebar-foreground",
          className,
        )}
        data-slot="sidebar"
        {...props}
      >
        {children}
      </div>
    );
  }

  if (isMobile) {
    return (
      <Sheet onOpenChange={setOpenMobile} open={openMobile} {...props}>
        <SheetPopup
          className="w-(--sidebar-width) bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden"
          data-mobile="true"
          data-sidebar="sidebar"
          data-slot="sidebar"
          side={side}
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH_MOBILE,
            } as React.CSSProperties
          }
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Sidebar</SheetTitle>
            <SheetDescription>Displays the mobile sidebar.</SheetDescription>
          </SheetHeader>
          <div className="flex h-full w-full flex-col">{children}</div>
        </SheetPopup>
      </Sheet>
    );
  }

  return (
    <div
      className="group peer hidden text-sidebar-foreground md:block"
      data-collapsible={state === "collapsed" ? collapsible : ""}
      data-side={side}
      data-slot="sidebar"
      data-state={state}
      data-variant={variant}
    >
      {/* This is what handles the sidebar gap on desktop */}
      <div
        className={sidebarGapVariants({ variant })}
        data-slot="sidebar-gap"
      />
      <div
        className={cn(sidebarContainerVariants({ side, variant }), className)}
        data-slot="sidebar-container"
        {...props}
      >
        <div
          className="flex h-full w-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow-sm/5"
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export function SidebarTrigger({
  className,
  onClick,
  ...props
}: React.ComponentProps<typeof Button>): React.ReactElement {
  const { toggleSidebar, isMobile, open, openMobile } = useSidebar();
  const sidebarOpen = isMobile ? openMobile : open;

  return (
    <Button
      className={cn("size-7", className)}
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        toggleSidebar();
      }}
      size="icon"
      variant="ghost"
      {...props}
    >
      {/* Registry artwork is swapped: `drawer-closed-*` draws the open drawer. */}
      <Icon name={sidebarOpen ? "drawer-closed-left" : "drawer-open-left"} />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
}

export function SidebarRail({
  className,
  ...props
}: React.ComponentProps<"button">): React.ReactElement {
  const { toggleSidebar } = useSidebar();

  return (
    <button
      aria-label="Toggle Sidebar"
      className={cn(
        "absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:after:bg-border group-data-[side=left]:-right-4 group-data-[side=right]:left-0 sm:flex",
        "in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize",
        "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
        "group-data-[collapsible=offcanvas]:translate-x-0 hover:group-data-[collapsible=offcanvas]:bg-sidebar group-data-[collapsible=offcanvas]:after:left-full",
        "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
        "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
        className,
      )}
      data-sidebar="rail"
      data-slot="sidebar-rail"
      onClick={toggleSidebar}
      tabIndex={-1}
      title="Toggle Sidebar"
      type="button"
      {...props}
    />
  );
}

export function SidebarInset({
  className,
  ...props
}: React.ComponentProps<"main">): React.ReactElement {
  return (
    <main
      className={cn(
        "relative flex w-full flex-1 flex-col bg-sidebar",
        "md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ms-2 md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ms-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm/5",
        className,
      )}
      data-slot="sidebar-inset"
      {...props}
    />
  );
}

export function SidebarInput({
  className,
  ...props
}: React.ComponentProps<typeof Input>): React.ReactElement {
  return (
    <Input
      className={cn("h-8 w-full bg-sidebar shadow-none", className)}
      data-sidebar="input"
      data-slot="sidebar-input"
      {...props}
    />
  );
}

export function SidebarHeader({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      className={cn("flex flex-col gap-2 p-2", className)}
      data-sidebar="header"
      data-slot="sidebar-header"
      {...props}
    />
  );
}

export function SidebarFooter({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      className={cn("flex flex-col gap-2 p-2", className)}
      data-sidebar="footer"
      data-slot="sidebar-footer"
      {...props}
    />
  );
}

export function SidebarSeparator({
  className,
  tone,
  ...props
}: React.ComponentProps<typeof Separator>): React.ReactElement {
  return (
    <Separator
      // Spans the pane's content column — the pane's gutter is the only inset.
      // The fill is the wrapper's only when the rule is the default hairline:
      // a `tone` paints from the primitive, and a class here would out-merge it.
      className={cn("w-auto", !tone && "bg-border", className)}
      data-sidebar="separator"
      data-slot="sidebar-separator"
      tone={tone}
      {...props}
    />
  );
}

export function SidebarContent({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <ScrollArea fill scrollFade>
      <div
        className={cn(
          "flex h-full flex-col gap-2 group-data-[collapsible=icon]:overflow-hidden",
          className,
        )}
        data-sidebar="content"
        data-slot="sidebar-content"
        {...props}
      />
    </ScrollArea>
  );
}

export function SidebarGroup({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      // Vertical rhythm only. The horizontal gutter belongs to the pane the
      // group sits in (`SidebarPane`), so a group never adds a second inset.
      className={cn("relative flex w-full min-w-0 flex-col py-2", className)}
      data-sidebar="group"
      data-slot="sidebar-group"
      {...props}
    />
  );
}

export function SidebarGroupLabel({
  className,
  render,
  ...props
}: useRender.ComponentProps<"div">): React.ReactElement {
  const defaultProps = {
    className: cn(
      "flex h-8 shrink-0 items-center rounded-sm px-2 font-medium text-muted-foreground text-xs outline-hidden inset-ring-ring transition-[margin,opacity] duration-base ease-linear focus-visible:inset-ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
      "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0",
      className,
    ),
    "data-sidebar": "group-label",
    "data-slot": "sidebar-group-label",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps(defaultProps, props),
    render,
  });
}

export function SidebarGroupAction({
  className,
  render,
  ...props
}: useRender.ComponentProps<"button">): React.ReactElement {
  const defaultProps = {
    className: cn(
      // right-1 matches SidebarMenuAction so the group "+" and the row "…"
      // share one vertical axis. Both actions use circular hover targets.
      // Keep the focus ring inset so a tight row does not clip it.
      "absolute top-3.5 right-1 flex aspect-square w-5 items-center justify-center rounded-full p-0 text-sidebar-foreground outline-hidden inset-ring-ring transition-transform hover:bg-wash-hover hover:text-sidebar-foreground focus-visible:inset-ring-2 [&>svg:not([class*='size-'])]:size-4 [&>svg]:shrink-0",
      // A coarse pointer gets an inflated hit area around the 20px square —
      // keyed on pointer capability, never on viewport width.
      "pointer-coarse:after:absolute pointer-coarse:after:-inset-2",
      "group-data-[collapsible=icon]:hidden",
      className,
    ),
    "data-sidebar": "group-action",
    "data-slot": "sidebar-group-action",
  };

  return useRender({
    defaultTagName: "button",
    props: mergeProps(defaultProps, props),
    render,
  });
}

export function SidebarGroupContent({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      className={cn("w-full text-sm", className)}
      data-sidebar="group-content"
      data-slot="sidebar-group-content"
      {...props}
    />
  );
}

export function SidebarMenu({
  className,
  ...props
}: React.ComponentProps<"ul">): React.ReactElement {
  return (
    <ul
      className={cn("flex w-full min-w-0 flex-col gap-0.5", className)}
      data-sidebar="menu"
      data-slot="sidebar-menu"
      {...props}
    />
  );
}

export function SidebarMenuItem({
  className,
  ...props
}: React.ComponentProps<"li">): React.ReactElement {
  return (
    <li
      className={cn("group/menu-item relative", className)}
      data-sidebar="menu-item"
      data-slot="sidebar-menu-item"
      {...props}
    />
  );
}

export function SidebarMenuButton({
  isActive = false,
  variant = "default",
  size = "default",
  tooltip,
  className,
  render,
  ...props
}: useRender.ComponentProps<"button"> & {
  isActive?: boolean;
  tooltip?: string | React.ComponentProps<typeof TooltipPopup>;
} & VariantProps<typeof sidebarMenuButtonVariants>): React.ReactElement {
  const { isMobile, state } = useSidebar();

  const defaultProps = {
    className: cn(sidebarMenuButtonVariants({ size, variant }), className),
    "data-active": isActive,
    // The active row is the page on screen, for a screen reader as for the eye.
    "aria-current": isActive ? ("page" as const) : undefined,
    "data-sidebar": "menu-button",
    "data-size": size,
    "data-slot": "sidebar-menu-button",
    style: { [ROW_VISUAL_VAR]: `${AVATAR_SIZE_PX.xs}px` } as React.CSSProperties,
  };

  const buttonProps = mergeProps<"button">(defaultProps, props);

  const buttonElement = useRender({
    defaultTagName: "button",
    props: buttonProps,
    render,
  });

  if (!tooltip) {
    return buttonElement;
  }

  if (typeof tooltip === "string") {
    tooltip = {
      children: tooltip,
    };
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={buttonElement as React.ReactElement<Record<string, unknown>>}
      />
      <TooltipPopup
        align="center"
        hidden={state !== "collapsed" || isMobile}
        side="right"
        {...tooltip}
      />
    </Tooltip>
  );
}

export function SidebarMenuAction({
  className,
  showOnHover = false,
  render,
  ...props
}: useRender.ComponentProps<"button"> & Omittable<{
  showOnHover?: boolean;
}>): React.ReactElement {
  const defaultProps = {
    className: cn(
      // size-5 is fixed, not aspect-square: an aspect ratio is only a
      // preferred size, so text content (24px line-height) would stretch the
      // box past the per-size top offsets that center it in the row.
      "absolute top-1 right-1 flex size-5 items-center justify-center overflow-hidden rounded-full p-0 text-sidebar-foreground outline-hidden inset-ring-ring transition-transform hover:bg-wash-hover hover:text-sidebar-foreground focus-visible:inset-ring-2 peer-hover/menu-button:text-sidebar-foreground [&>svg:not([class*='size-'])]:size-4 [&>svg]:shrink-0",
      // A coarse pointer gets an inflated hit area around the 20px square —
      // keyed on pointer capability, never on viewport width.
      "pointer-coarse:after:absolute pointer-coarse:after:-inset-2",
      // The action is a 20px square; each top centers it in that size's row
      // height: sm/default h-7 → (28−20)/2, lg h-12 → (48−20)/2.
      "peer-data-[size=sm]/menu-button:top-1",
      "peer-data-[size=default]/menu-button:top-1",
      "peer-data-[size=lg]/menu-button:top-3.5",
      "group-data-[collapsible=icon]:hidden",
      // Hover-revealed on the row; keyboard focus and an open popup hold it.
      // A coarse pointer never sees it: a nav row's tap is its navigation and
      // must not pay a reveal, and a column of standing "…" is noise — the
      // row's actions live behind a held press there (the row is a context
      // menu trigger). Hidden outright, not faded, so the inflated hit area
      // above cannot catch a tap meant for the row.
      showOnHover &&
        "opacity-0 group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-popup-open:opacity-100 pointer-coarse:hidden peer-data-[active=true]/menu-button:text-sidebar-foreground",
      className,
    ),
    "data-sidebar": "menu-action",
    // The row button pads for a hover-revealed action only while it shows;
    // this attribute is the selector it keys on.
    "data-reveal": showOnHover ? "hover" : undefined,
    "data-slot": "sidebar-menu-action",
  };

  return useRender({
    defaultTagName: "button",
    props: mergeProps<"button">(defaultProps, props),
    render,
  });
}

export function SidebarMenuBadge({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      className={cn(
        "pointer-events-none absolute right-1 flex h-5 min-w-5 select-none items-center justify-center rounded-lg px-1 font-medium text-sidebar-foreground text-xs tabular-nums",
        "peer-hover/menu-button:text-sidebar-foreground peer-data-[active=true]/menu-button:text-sidebar-foreground",
        // The badge is h-5 (20px); each top centers it in that size's row
        // height: sm/default h-7 → (28−20)/2, lg h-12 → (48−20)/2.
        "peer-data-[size=sm]/menu-button:top-1",
        "peer-data-[size=default]/menu-button:top-1",
        "peer-data-[size=lg]/menu-button:top-3.5",
        "group-data-[collapsible=icon]:hidden",
        className,
      )}
      data-sidebar="menu-badge"
      data-slot="sidebar-menu-badge"
      {...props}
    />
  );
}

export function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}: React.ComponentProps<"div"> & Omittable<{
  showIcon?: boolean;
}>): React.ReactElement {
  // Random width between 50 to 90%.
  const width = React.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, []);

  return (
    <div
      className={cn("flex h-8 items-center gap-2 rounded-lg px-2", className)}
      data-sidebar="menu-skeleton"
      data-slot="sidebar-menu-skeleton"
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-lg"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 max-w-(--skeleton-width) flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            "--skeleton-width": width,
          } as React.CSSProperties
        }
      />
    </div>
  );
}

export function SidebarMenuSub({
  className,
  ...props
}: React.ComponentProps<"ul">): React.ReactElement {
  return (
    <ul
      className={cn(
        "mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-sidebar-border border-l px-2.5 py-0.5",
        "group-data-[collapsible=icon]:hidden",
        className,
      )}
      data-sidebar="menu-sub"
      data-slot="sidebar-menu-sub"
      {...props}
    />
  );
}

export function SidebarMenuSubItem({
  className,
  ...props
}: React.ComponentProps<"li">): React.ReactElement {
  return (
    <li
      className={cn("group/menu-sub-item relative", className)}
      data-sidebar="menu-sub-item"
      data-slot="sidebar-menu-sub-item"
      {...props}
    />
  );
}

export const sidebarMenuSubButtonVariants = cva(
  cn(
    "flex h-8 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-sm px-2 text-sidebar-foreground outline-hidden inset-ring-ring hover:bg-wash-hover focus-visible:inset-ring-2 active:bg-wash-active active:text-sidebar-foreground disabled:pointer-events-none disabled:opacity-64 aria-disabled:pointer-events-none aria-disabled:opacity-64 sm:h-7 [&>span:last-child]:truncate [&>svg:not([class*='size-'])]:size-4 [&>svg]:shrink-0",
    // Same three-rung glyph ramp as SidebarMenuButton: rest below the muted
    // label, hover to the label's ink, active to the full foreground —
    // explicitly-colored icons (`text-` classes) excepted.
    "[&>svg:not([class^='text-']):not([class*='_text-'])]:text-muted-foreground not-data-[active=true]:hover:[&>svg:not([class^='text-']):not([class*='_text-'])]:text-muted-foreground data-[active=true]:[&>svg:not([class^='text-']):not([class*='_text-'])]:text-sidebar-foreground",
    "data-[active=true]:bg-wash-selected data-[active=true]:text-sidebar-foreground",
    "group-data-[collapsible=icon]:hidden",
  ),
  {
    defaultVariants: {
      size: "md",
    },
    variants: {
      size: {
        sm: "text-xs",
        md: "text-sm",
      },
    },
  },
);

export function SidebarMenuSubButton({
  size = "md",
  isActive = false,
  className,
  render,
  ...props
}: useRender.ComponentProps<"a"> & Omittable<{
  isActive?: boolean;
}> & VariantProps<typeof sidebarMenuSubButtonVariants>): React.ReactElement {
  const defaultProps = {
    className: cn(sidebarMenuSubButtonVariants({ size }), className),
    "data-active": isActive,
    "data-sidebar": "menu-sub-button",
    "data-size": size,
    "data-slot": "sidebar-menu-sub-button",
  };

  return useRender({
    defaultTagName: "a",
    props: mergeProps<"a">(defaultProps, props),
    render,
  });
}
